'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import Link from 'next/link'
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bot,
  CheckCircle2,
  Clock,
  Database,
  MessageSquare,
  Shield,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
  XCircle,
  HelpCircle,
  MapPin,
  Phone,
  UserX,
} from 'lucide-react'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: 'easeOut' as const },
  }),
}

// ============================================================
// DADOS DO RELATÓRIO
// ============================================================

const metricas = [
  { valor: '8.210', label: 'contatos na base', icon: Users, cor: 'text-blue-600', bgCor: 'bg-blue-50' },
  { valor: '53,5%', label: 'conversas sem resposta', icon: AlertTriangle, cor: 'text-red-600', bgCor: 'bg-red-50' },
  { valor: '1.190', label: 'cessados vs 33 entrantes', icon: TrendingDown, cor: 'text-amber-600', bgCor: 'bg-amber-50' },
  { valor: '65%', label: 'das perguntas automatizaveis', icon: Bot, cor: 'text-emerald-600', bgCor: 'bg-emerald-50' },
]

const diagnosticoConfirmado = [
  {
    dor: 'Revendedoras abandonadas',
    evidencia: '99 conversas abertas sem ninguem atribuido. Das 86 atribuidas, 80 estao com uma unica pessoa (Raissa). Revendedoras fazem perguntas simples e ficam sem resposta.',
    icon: UserX,
    gravidade: 'critico',
  },
  {
    dor: 'Dependencia da Raissa',
    evidencia: 'Raissa responde 93% de todas as conversas atribuidas (80 de 86). Os outros 4 atendentes somam 6 conversas. Quando ela para, o atendimento para.',
    icon: Shield,
    gravidade: 'critico',
  },
  {
    dor: 'CRM fechado, dados presos',
    evidencia: 'A API publica do ManyChat nao expoe historico de conversas. Precisamos extrair via navegador. Dados de desempenho por revendedora nao existem no ManyChat.',
    icon: Database,
    gravidade: 'alto',
  },
  {
    dor: 'Base encolhendo sem controle',
    evidencia: '1.190 cessados (14,5% da base) contra apenas 33 entrantes (0,4%). Zero growth tools configurados. Instagram e Messenger sem automacao. A base perde 36x mais do que ganha.',
    icon: TrendingDown,
    gravidade: 'critico',
  },
]

const niveisBase = [
  { nivel: 'A0 (entrada)', contatos: 1725, pct: 21.0 },
  { nivel: 'A1', contatos: 1610, pct: 19.6 },
  { nivel: 'A2', contatos: 2055, pct: 25.0 },
  { nivel: 'A3', contatos: 1093, pct: 13.3 },
  { nivel: 'I4', contatos: 315, pct: 3.8 },
  { nivel: 'I5', contatos: 269, pct: 3.3 },
  { nivel: 'I6', contatos: 211, pct: 2.6 },
  { nivel: 'Cessados', contatos: 1190, pct: 14.5, destaque: true },
  { nivel: 'Entrantes', contatos: 33, pct: 0.4, destaque: true },
]

const supervisoras = [
  { nome: 'Joao-Guilherme', contatos: 563 },
  { nome: 'Byanca', contatos: 563 },
  { nome: 'Marcos', contatos: 550 },
  { nome: 'Debora', contatos: 544 },
  { nome: 'Livia', contatos: 543 },
  { nome: 'Barbara', contatos: 521 },
  { nome: 'Franciele', contatos: 509 },
  { nome: 'Lucas', contatos: 488 },
  { nome: 'Mariana', contatos: 487 },
  { nome: 'Raissa', contatos: 478 },
  { nome: 'Daiana', contatos: 477 },
  { nome: 'Larissa', contatos: 449 },
  { nome: 'Joao Gabriel', contatos: 446 },
  { nome: 'Leticia-Lima', contatos: 136 },
]

const automacoesLive = [
  { nome: 'Quero ver catalogos', execucoes: 369, ctr: '18,9%', status: 'live' },
  { nome: 'Preciso de ajuda', execucoes: 34, ctr: '0%', status: 'live' },
  { nome: 'Falar Supervisora', execucoes: 2, ctr: 'N/A', status: 'live' },
]

const automacoesStopped = [
  { nome: 'WhatsApp Default Reply', execucoes: 7, obs: 'Novos contatos sem resposta automatica' },
  { nome: 'Instagram Default Reply', execucoes: 0, obs: 'Canal IG totalmente sem automacao' },
  { nome: 'Quero cards para meus clientes', execucoes: 26, obs: 'Ferramenta de vendas desligada' },
  { nome: 'Ofertas da semana', execucoes: 34, obs: 'Promocoes desligadas' },
  { nome: 'Catalogo Oui', execucoes: 0, obs: 'Nunca foi usada' },
  { nome: 'Catalogo O Boticario', execucoes: 1, obs: 'Praticamente nao usada' },
  { nome: 'Catalogo Eudora', execucoes: 0, obs: 'Desligada' },
]

const perguntasFrequentes = [
  { pergunta: 'Que horas? / Qual horario?', frequencia: 'Muito alta', automatizavel: true, solucao: 'Incluir horario direto na mensagem do evento' },
  { pergunta: 'Onde e? / Qual endereco?', frequencia: 'Muito alta', automatizavel: true, solucao: 'Incluir endereco + link Google Maps no convite' },
  { pergunta: 'O que precisa? / Como participo?', frequencia: 'Alta', automatizavel: true, solucao: 'FAQ automatico com regras claras' },
  { pergunta: 'Tem que ser desses produtos?', frequencia: 'Alta', automatizavel: true, solucao: 'Esclarecer na mensagem: qualquer item' },
  { pergunta: 'Tem valor minimo?', frequencia: 'Alta', automatizavel: true, solucao: 'Incluir na regra: nao tem valor minimo' },
  { pergunta: 'O kit conta como dois?', frequencia: 'Media', automatizavel: true, solucao: 'FAQ com regras de contagem' },
  { pergunta: 'E pago?', frequencia: 'Media', automatizavel: true, solucao: 'Esclarecer: e gratuito, basta comprar X itens' },
  { pergunta: 'Posso levar alguem?', frequencia: 'Media', automatizavel: true, solucao: 'Incluir politica de acompanhantes' },
  { pergunta: 'Quando chega meu pedido?', frequencia: 'Baixa', automatizavel: false, solucao: 'Precisa de integracao com sistema de pedidos' },
  { pergunta: 'Nao consigo entrar no app', frequencia: 'Baixa', automatizavel: true, solucao: 'Link para suporte Boticario + passo a passo' },
]

const campanhasPorMes = [
  {
    mes: 'Janeiro 2026',
    campanhas: 'dia 22/01, 23/01, 27/01, 27/01 eudora',
    contatosAlcancados: '1.019 + 663 + 374 + 247',
    tipoAcao: 'Encontros de ciclo por cidade + Eudora',
    padroesIdentificados: [
      'Primeiras automacoes da conta - volume moderado',
      'Sem vale desconto ou brinde ainda - foco em encontro presencial',
      'Contatos receberam tag de campanha mas baixo engajamento em respostas',
      'Supervisoras ainda sem base nova definida',
    ],
    problemas: 'Automacoes basicas, sem segmentacao por nivel. Conversas de janeiro mostram pouca interacao humana.',
  },
  {
    mes: 'Fevereiro 2026',
    campanhas: 'VALE CASHBACK 8 FEV, 09/02, 11/02 fran, BINGO, CONFIRMADOS BINGO',
    contatosAlcancados: '1.798 + 29 + 149 + 94 + 52',
    tipoAcao: 'Vale desconto R$25 + Cashback + Bingos presenciais',
    padroesIdentificados: [
      'Introducao do Vale Desconto de R$25 - alta adesao (1.798 contatos)',
      'Follow-up com urgencia: "Ele se encerra em 4 horas!"',
      'Cashback com condicionante: "Comprando R$199,90 nesse ciclo"',
      'Raissa pausava automacao manualmente para atender duvidas do vale',
      'Bingos como evento engajador - 52 confirmados de 94 convidados (55% conversao)',
    ],
    problemas: 'Muitas revendedoras receberam vale mas nao usaram (sem follow-up de recuperacao). Raissa ja sobrecarregada com atendimento manual.',
  },
  {
    mes: 'Marco 2026',
    campanhas: 'O MAIOR NIVER, SIAGE DAY, DIA D OUI, a3/a4 23, BASES NOVAS, ENCONTROS DE CICLO',
    contatosAlcancados: '1.610 + 1.405 + 2.224 + 626 + 323+322+322',
    tipoAcao: 'Eventos presenciais grandes + Redistribuicao de bases + Lancamentos',
    padroesIdentificados: [
      'Mes com mais campanhas simultaneas - 6+ automacoes ativas',
      'Redistribuicao de supervisoras: "Sou sua nova supervisora!" (Mariana, Larissa, Raissa, Daiana, Byanca)',
      'Mensagens de reativacao: "Voce sumiu no mes passado!" para inativas',
      'O MAIOR NIVER com spoiler strategy: "Posso te dar um spoiler? Teremos BRINDES DINAMICAS COFFEE"',
      'DIA D OUI com precos detalhados: codigo + preco custo + preco venda + lucro',
      'Contatada pediu catalogo manualmente apesar de existir automacao',
    ],
    problemas: 'Excesso de mensagens - mesma revendedora recebe 3-4 campanhas no mesmo mes. Troca de supervisora causa confusao. Revendedora nao sabe quem eh sua supervisora nova.',
  },
  {
    mes: 'Abril 2026',
    campanhas: 'CABELO DAY, I4/I5/I6 HFVD E CCVD, I4/I5/I6 INFINITY',
    contatosAlcancados: '1.741 + 392 + 292',
    tipoAcao: 'Evento cabelereiro Dia das Maes + Promocoes por nivel',
    padroesIdentificados: [
      'Campanha mais engajadora: CABELO DAY com escova gratis',
      'Segmentacao por nivel comecou (I4-I6 separados de A0-A3)',
      'Automacao com 3 opcoes de produto (enquete interativa)',
      'Bug: Leliane clicou nas 3 opcoes e recebeu mesma mensagem 3 vezes',
      'Homem recebeu mensagem de cabelo feminino (Marcos: "Nem cabelo eu tenho")',
      'Convite de evento em cidade errada (Jacyra de Lorena recebeu Cruzeiro)',
      'Mensagens mais longas com parcelamento: "Parcela em 3x no boleto"',
      'Revendedora nao pode ir por conflito de agenda: "Hoje nao posso, eh dia de bingo"',
    ],
    problemas: 'Problemas de segmentacao por genero e cidade. Bug na automacao de enquete (resposta duplicada). 99 conversas sem resposta acumuladas.',
  },
]

const casosReais = [
  {
    nome: 'Marcos',
    problema: 'Automacao de cabelo feminino enviada para homem',
    mensagem: '"Nem cabelo eu tenho kkk"',
    causa: 'Sem segmentacao por genero nas automacoes',
    icon: UserX,
  },
  {
    nome: 'Jacyra',
    problema: 'Convite de evento em cidade errada',
    mensagem: '"Oi na onde e?" - Jacyra e de Lorena, evento era em Cruzeiro',
    causa: 'Sem filtro geografico nos disparos. Raissa corrigiu manualmente (boa pratica)',
    icon: MapPin,
  },
  {
    nome: 'Daniela',
    problema: 'Revendedora mandou propaganda do proprio negocio',
    mensagem: '"Espetinho da Dany - Clique no link"',
    causa: 'Canal confundido como WhatsApp pessoal',
    icon: MessageSquare,
  },
  {
    nome: 'Lucimara',
    problema: 'Multiplos problemas sem resolucao',
    mensagem: '"Nao lembro da senha", "Como eu faco", "Hj que chega meu pedido?"',
    causa: 'Sem fluxo de encaminhamento para problemas tecnicos ou logisticos',
    icon: HelpCircle,
  },
  {
    nome: 'Marlene',
    problema: 'Reclamacao de pedido perdido',
    mensagem: '"To chateada de perder meus pedidos - passei o pedido, demorei pra pegar, foi retirado"',
    causa: 'Sem SLA de retirada nem aviso automatico de prazo',
    icon: AlertTriangle,
  },
  {
    nome: 'Leliane',
    problema: 'Bug: clicou 3 opcoes e recebeu mesma mensagem 3 vezes',
    mensagem: 'Selecionou Opcao 1, 2 e 3 - recebeu "Sabia que essa promocao e exclusiva?" tres vezes identicas',
    causa: 'Automacao nao bloqueia resposta multipla. Cada clique dispara o mesmo fluxo',
    icon: AlertTriangle,
  },
  {
    nome: 'Carmem',
    problema: 'Pediu catalogo manualmente com automacao disponivel',
    mensagem: '"Vc poderia me enviar o catalogo da campanha atual?" - Raissa mandou PDFs na mao',
    causa: 'Revendedora nao sabe que existe botao de catalogo. Menu nao e visivel o suficiente',
    icon: HelpCircle,
  },
  {
    nome: 'Jociane',
    problema: 'Confusao com troca de supervisora',
    mensagem: '"Aqui e a Mariana, sou sua nova supervisora! Pode me enviar um oi? (12)992558761"',
    causa: 'Troca de supervisora via automacao sem contexto. Revendedora nao sabe por que mudou',
    icon: UserX,
  },
  {
    nome: 'Verena',
    problema: 'Revendedora consulta clientes antes de comprar',
    mensagem: '"Vou ver se alguem quer algo" - nao compra pra si, intermedia',
    causa: 'Automacao trata todas como consumidora final. Falta fluxo para revendedora-intermediaria',
    icon: MessageSquare,
  },
]

const proximosPassos = [
  { prioridade: 'Urgente', acao: 'Distribuir as 99 conversas nao atribuidas entre supervisoras', impacto: 'Revendedoras voltam a ter resposta imediata' },
  { prioridade: 'Urgente', acao: 'Reativar WhatsApp Default Reply com menu completo', impacto: 'Novos contatos nao ficam mais no vacuo' },
  { prioridade: 'Semana 1', acao: 'Incluir horario + endereco + regras nas mensagens de evento', impacto: 'Elimina 65% das perguntas para a Raissa' },
  { prioridade: 'Semana 1', acao: 'Adicionar segmentacao por genero e cidade nos disparos', impacto: 'Fim de erros como "cabelo" para homem' },
  { prioridade: 'Semana 2', acao: 'Configurar webhook para captura de mensagens em tempo real', impacto: 'Dashboard de atendimento com dados reais' },
  { prioridade: 'Semana 2', acao: 'Criar campanha de reativacao para os 1.190 cessados', impacto: 'Potencial de recuperar 120-240 revendedoras' },
  { prioridade: 'Semana 3', acao: 'Ativar growth tools e automacao do Instagram', impacto: 'Aumentar entrada de novos contatos (hoje: 33)' },
]

// ============================================================
// COMPONENTES AUXILIARES
// ============================================================

function StatCard({ metrica, i }: { metrica: typeof metricas[0]; i: number }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUp}
      custom={i}
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <div className={`mb-3 inline-flex rounded-xl ${metrica.bgCor} p-3`}>
        <metrica.icon className={`h-6 w-6 ${metrica.cor}`} />
      </div>
      <p className="text-3xl font-bold text-slate-900">{metrica.valor}</p>
      <p className="mt-1 text-sm text-slate-600">{metrica.label}</p>
    </motion.div>
  )
}

function GravidadeBadge({ gravidade }: { gravidade: string }) {
  const cores: Record<string, string> = {
    critico: 'bg-red-100 text-red-700',
    alto: 'bg-amber-100 text-amber-700',
    medio: 'bg-yellow-100 text-yellow-700',
  }
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${cores[gravidade] || cores.medio}`}>
      {gravidade}
    </span>
  )
}

// ============================================================
// PAGINA
// ============================================================

// ============================================================
// DASHBOARD DEMO
// ============================================================

const dashTabs = ['Visao Geral', 'Campanhas', 'Por Central', 'Inbox'] as const

const campanhasDemo = [
  { nome: 'CABELO DAY', status: 'Ativa', enviados: 1741, lidos: 1218, clicados: 487, convertidos: 156 },
  { nome: 'I4-I6 INFINITY', status: 'Ativa', enviados: 292, lidos: 219, clicados: 102, convertidos: 41 },
  { nome: 'O MAIOR NIVER', status: 'Encerrada', enviados: 1610, lidos: 1127, clicados: 548, convertidos: 203 },
  { nome: 'VALE CASHBACK FEV', status: 'Encerrada', enviados: 1798, lidos: 1259, clicados: 612, convertidos: 178 },
]

const centraisDemo = [
  { nome: 'Cruzeiro', revendedoras: 2847, supervisores: ['Joao-Guilherme', 'Byanca', 'Barbara', 'Debora', 'Marcos'], ativas: 2412, cessadas: 435 },
  { nome: 'Guaratingueta', revendedoras: 3012, supervisores: ['Livia', 'Franciele', 'Lucas', 'Mariana', 'Raissa'], ativas: 2540, cessadas: 472 },
  { nome: 'Cacapava', revendedoras: 2351, supervisores: ['Daiana', 'Larissa', 'Joao Gabriel', 'Leticia-Lima'], ativas: 2068, cessadas: 283 },
]

const inboxDemo = [
  { nome: 'Maysa', msg: 'Obrigado, so isso mesmo', tempo: '15h', ia: false, status: 'Resolvida' },
  { nome: 'Lucimara', msg: 'Como eu faco pra entrar no app?', tempo: '22h', ia: true, status: 'IA ativa' },
  { nome: 'Sandra', msg: 'Que horas o evento?', tempo: '1d', ia: true, status: 'IA ativa' },
  { nome: 'Marcos', msg: 'Quero ir no evento', tempo: '1d', ia: false, status: 'Aberta' },
  { nome: 'Jacyra', msg: 'Me manda o endereco?', tempo: '1d', ia: true, status: 'IA ativa' },
]

function DashboardDemo() {
  const [tab, setTab] = useState<typeof dashTabs[number]>('Visao Geral')
  return (
    <section className="bg-slate-900 px-6 py-20 text-white">
      <div className="mx-auto max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
          <div className="mb-2 inline-block rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-400">
            Preview do entregavel
          </div>
          <h2 className="mb-4 text-3xl font-bold md:text-4xl">Seu dashboard</h2>
          <p className="mb-8 max-w-3xl text-lg text-slate-400">
            Mockup interativo com seus dados reais. Este e o painel que voce vai acessar
            todos os dias para acompanhar a operacao.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {dashTabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                tab === t ? 'bg-rose-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="rounded-2xl border border-slate-700 bg-slate-800/50 p-6">
          {tab === 'Visao Geral' && (
            <div>
              <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { v: '8.210', l: 'Total revendedoras', c: 'text-white' },
                  { v: '7.020', l: 'Ativas', c: 'text-emerald-400' },
                  { v: '1.190', l: 'Cessadas', c: 'text-red-400' },
                  { v: '33', l: 'Entrantes (mes)', c: 'text-amber-400' },
                ].map((s) => (
                  <div key={s.l} className="rounded-xl bg-slate-700/50 p-4">
                    <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
                    <p className="mt-1 text-xs text-slate-400">{s.l}</p>
                  </div>
                ))}
              </div>
              <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 p-4">
                <p className="text-sm font-medium text-red-400">99 conversas sem resposta - requer atencao imediata</p>
              </div>
              <p className="mb-3 text-sm font-semibold text-slate-300">Distribuicao por nivel de atividade</p>
              <div className="space-y-2">
                {niveisBase.map((n) => (
                  <div key={n.nivel} className="flex items-center gap-3">
                    <span className="w-24 text-xs text-slate-400">{n.nivel}</span>
                    <div className="flex-1">
                      <div className="h-3 w-full rounded-full bg-slate-700">
                        <div className={`h-3 rounded-full ${n.destaque ? 'bg-red-500' : 'bg-rose-500'}`} style={{ width: `${Math.min((n.pct / 25) * 100, 100)}%` }} />
                      </div>
                    </div>
                    <span className="w-16 text-right text-xs text-slate-400">{n.contatos.toLocaleString('pt-BR')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'Campanhas' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-slate-400">
                    <th className="py-3 text-left font-medium">Campanha</th>
                    <th className="py-3 text-center font-medium">Status</th>
                    <th className="py-3 text-right font-medium">Enviados</th>
                    <th className="py-3 text-right font-medium">Lidos</th>
                    <th className="py-3 text-right font-medium">Clicados</th>
                    <th className="py-3 text-right font-medium">Convertidos</th>
                    <th className="py-3 text-right font-medium">Taxa</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {campanhasDemo.map((c) => (
                    <tr key={c.nome} className="text-slate-300">
                      <td className="py-3 font-medium text-white">{c.nome}</td>
                      <td className="py-3 text-center">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${c.status === 'Ativa' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'}`}>{c.status}</span>
                      </td>
                      <td className="py-3 text-right">{c.enviados.toLocaleString('pt-BR')}</td>
                      <td className="py-3 text-right">{c.lidos.toLocaleString('pt-BR')}</td>
                      <td className="py-3 text-right">{c.clicados.toLocaleString('pt-BR')}</td>
                      <td className="py-3 text-right text-emerald-400">{c.convertidos.toLocaleString('pt-BR')}</td>
                      <td className="py-3 text-right text-amber-400">{((c.convertidos / c.enviados) * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-4 text-xs text-slate-500">Disparo assertivo: envio → leitura → clique → compra. Exatamente o que voce pediu.</p>
            </div>
          )}

          {tab === 'Por Central' && (
            <div className="grid gap-4 md:grid-cols-3">
              {centraisDemo.map((c) => (
                <div key={c.nome} className="rounded-xl border border-slate-700 bg-slate-700/30 p-5">
                  <h3 className="mb-1 text-lg font-bold text-white">{c.nome}</h3>
                  <div className="mb-3 flex gap-4 text-sm">
                    <span className="text-emerald-400">{c.ativas} ativas</span>
                    <span className="text-red-400">{c.cessadas} cessadas</span>
                  </div>
                  <p className="mb-2 text-xs font-medium text-slate-400">Supervisores:</p>
                  <div className="flex flex-wrap gap-1">
                    {c.supervisores.map((s) => (
                      <span key={s} className="rounded bg-slate-600/50 px-2 py-0.5 text-xs text-slate-300">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'Inbox' && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm font-medium text-slate-300">Ultimas conversas - Funil Chatbot</p>
                <span className="rounded bg-emerald-500/20 px-2 py-1 text-xs font-medium text-emerald-400">app.funilchatbot.com.br</span>
              </div>
              <div className="space-y-2">
                {inboxDemo.map((c) => (
                  <div key={c.nome} className="flex items-center gap-4 rounded-lg bg-slate-700/30 p-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-600 text-sm font-bold text-white">
                      {c.nome[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{c.nome}</span>
                        <span className="text-xs text-slate-500">{c.tempo}</span>
                      </div>
                      <p className="truncate text-sm text-slate-400">{c.msg}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      c.status === 'IA ativa' ? 'bg-purple-500/20 text-purple-400' :
                      c.status === 'Resolvida' ? 'bg-emerald-500/20 text-emerald-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>{c.status}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-500">As perguntas sobre horario, endereco e regras sao respondidas automaticamente pela IA. Supervisora so entra quando necessario.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

// ============================================================
// PAGINA
// ============================================================

export default function Relatorio1Page() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-rose-950 px-6 py-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(236,72,153,0.15),transparent_50%)]" />
        <div className="relative mx-auto max-w-5xl">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm backdrop-blur">
              <BarChart3 className="h-4 w-4 text-rose-300" />
              <span>Relatorio 1 - Diagnostico Operacional</span>
            </div>
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-6xl">
              Auditoria ManyChat<br />
              Espaco do Revendedor
            </h1>
            <p className="max-w-3xl text-xl text-slate-300">
              Analise completa da conta ManyChat com 8.210 contatos: segmentacao, automacoes,
              padroes de atendimento e historico de conversas. Dados extraidos via API + navegacao
              direta no inbox.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-300">
              <span className="rounded-full bg-white/10 px-4 py-2">Para: Raissa Menezes Bustamante</span>
              <span className="rounded-full bg-white/10 px-4 py-2">Por: Jonathan Machado | eBuz</span>
              <span className="rounded-full bg-white/10 px-4 py-2">12 de abril de 2026</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* RESUMO EXECUTIVO */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Resumo executivo</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Quatro numeros que resumem o estado atual da operacao de atendimento no ManyChat.
            </p>
          </motion.div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {metricas.map((m, i) => (
              <StatCard key={m.label} metrica={m} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* DIAGNOSTICO CONFIRMADO */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-2 inline-block rounded-full bg-red-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-red-700">
              Validacao com dados reais
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Diagnostico confirmado</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Cada dor identificada na proposta foi validada com dados reais extraidos do ManyChat.
              Nenhuma suposicao - todos os numeros abaixo vem da conta.
            </p>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-2">
            {diagnosticoConfirmado.map((d, i) => (
              <motion.div
                key={d.dor}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="mb-4 flex items-center justify-between">
                  <d.icon className="h-10 w-10 text-rose-600" />
                  <GravidadeBadge gravidade={d.gravidade} />
                </div>
                <h3 className="mb-2 text-xl font-semibold">{d.dor}</h3>
                <p className="text-slate-600">{d.evidencia}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MAPA DA BASE */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Mapa da base</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              8.210 contatos segmentados por nivel de atividade e supervisora responsavel.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {/* Niveis */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
              <h3 className="mb-4 text-xl font-semibold">Niveis de atividade</h3>
              <div className="overflow-hidden rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Nivel</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">Contatos</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700">%</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {niveisBase.map((n) => (
                      <tr key={n.nivel} className={n.destaque ? 'bg-red-50' : 'hover:bg-slate-50'}>
                        <td className={`px-4 py-3 font-medium ${n.destaque ? 'text-red-700' : 'text-slate-900'}`}>{n.nivel}</td>
                        <td className={`px-4 py-3 text-right ${n.destaque ? 'font-bold text-red-700' : 'text-slate-700'}`}>{n.contatos.toLocaleString('pt-BR')}</td>
                        <td className="px-4 py-3 text-right text-slate-500">{n.pct}%</td>
                        <td className="px-4 py-3">
                          <div className="h-2 w-full rounded-full bg-slate-100">
                            <div
                              className={`h-2 rounded-full ${n.destaque ? 'bg-red-400' : 'bg-rose-500'}`}
                              style={{ width: `${Math.min((n.pct / 25) * 100, 100)}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Supervisoras */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <h3 className="mb-4 text-xl font-semibold">14 supervisoras ({supervisoras.reduce((a, s) => a + s.contatos, 0).toLocaleString('pt-BR')} contatos)</h3>
              <div className="overflow-hidden rounded-xl border border-slate-200">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">Supervisor(a)</th>
                        <th className="px-4 py-3 text-right font-semibold text-slate-700">Contatos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {supervisoras.map((s) => (
                        <tr key={s.nome} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 text-slate-900">{s.nome}</td>
                          <td className="px-4 py-2.5 text-right text-slate-700">{s.contatos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-500">
                ~1.456 contatos (17,7%) nao tem supervisor atribuido.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AUTOMACOES */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Automacoes</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              O que funciona e o que esta parado. Das 10+ automacoes, apenas 3 estao ativas.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2">
            {/* Live */}
            <div>
              <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Ativas (LIVE)
              </h3>
              <div className="space-y-3">
                {automacoesLive.map((a, i) => (
                  <motion.div
                    key={a.nome}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i}
                    className="rounded-xl border border-emerald-200 bg-white p-4"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900">{a.nome}</span>
                      <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">LIVE</span>
                    </div>
                    <div className="mt-2 flex gap-4 text-sm text-slate-500">
                      <span>{a.execucoes} execucoes</span>
                      <span>CTR: {a.ctr}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Stopped */}
            <div>
              <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold">
                <XCircle className="h-5 w-5 text-red-500" /> Paradas (STOPPED)
              </h3>
              <div className="space-y-3">
                {automacoesStopped.map((a, i) => (
                  <motion.div
                    key={a.nome}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i}
                    className="rounded-xl border border-red-100 bg-white p-4"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900">{a.nome}</span>
                      <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-600">STOPPED</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{a.obs}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EVOLUCAO POR CAMPANHA */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-2 inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
              Jan - Abr 2026 | 20+ conversas analisadas por campanha
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Evolucao por campanha</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Analise das campanhas mes a mes para evitar viés de amostra.
              Cada campanha foi rastreada pela tag no ManyChat e as conversas
              foram lidas individualmente.
            </p>
          </motion.div>

          <div className="space-y-8">
            {campanhasPorMes.map((c, i) => (
              <motion.div
                key={c.mes}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-xl font-bold text-slate-900">{c.mes}</h3>
                  <span className="text-sm text-slate-500">Contatos alcancados: {c.contatosAlcancados}</span>
                </div>
                <div className="mb-4 flex flex-wrap gap-2">
                  {c.campanhas.split(', ').map((tag) => (
                    <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{tag}</span>
                  ))}
                </div>
                <p className="mb-3 text-sm font-medium text-slate-700">Tipo de acao: {c.tipoAcao}</p>
                <div className="mb-4">
                  <p className="mb-2 text-sm font-semibold text-slate-700">Padroes identificados:</p>
                  <ul className="space-y-1">
                    {c.padroesIdentificados.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg bg-amber-50 p-3">
                  <p className="text-sm text-amber-800"><strong>Problemas:</strong> {c.problemas}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PADROES DE ATENDIMENTO */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-2 inline-block rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-rose-700">
              Baseado em 15 conversas completas + 30 previews
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Padroes de atendimento</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              As 10 perguntas mais frequentes das revendedoras. 8 de 10 podem ser
              respondidas automaticamente com informacoes mais completas nas mensagens iniciais.
            </p>
          </motion.div>

          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">#</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Pergunta da revendedora</th>
                    <th className="px-4 py-3 text-center font-semibold text-slate-700">Frequencia</th>
                    <th className="px-4 py-3 text-center font-semibold text-slate-700">Bot resolve?</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Solucao proposta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {perguntasFrequentes.map((p, i) => (
                    <tr key={p.pergunta} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{p.pergunta}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          p.frequencia === 'Muito alta' ? 'bg-red-100 text-red-700' :
                          p.frequencia === 'Alta' ? 'bg-amber-100 text-amber-700' :
                          p.frequencia === 'Media' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {p.frequencia}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {p.automatizavel ? (
                          <CheckCircle2 className="mx-auto h-5 w-5 text-emerald-500" />
                        ) : (
                          <XCircle className="mx-auto h-5 w-5 text-slate-300" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{p.solucao}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CASOS REAIS */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Casos reais encontrados</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Problemas de segmentacao e atendimento identificados nas conversas.
              Cada caso mostra uma oportunidade de melhoria concreta.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {casosReais.map((c, i) => (
              <motion.div
                key={c.nome}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-3">
                  <c.icon className="h-8 w-8 text-rose-600" />
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">{c.nome}</span>
                </div>
                <h3 className="mb-2 font-semibold text-slate-900">{c.problema}</h3>
                <p className="mb-3 rounded-lg bg-amber-50 p-3 text-sm italic text-amber-800">{c.mensagem}</p>
                <p className="text-sm text-slate-600"><strong>Causa:</strong> {c.causa}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROXIMOS PASSOS */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Proximos passos</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Acoes priorizadas por urgencia. As duas primeiras podem ser executadas hoje.
            </p>
          </motion.div>

          <div className="space-y-4">
            {proximosPassos.map((p, i) => (
              <motion.div
                key={p.acao}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center"
              >
                <span className={`inline-flex w-fit shrink-0 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                  p.prioridade === 'Urgente' ? 'bg-red-100 text-red-700' :
                  p.prioridade === 'Semana 1' ? 'bg-amber-100 text-amber-700' :
                  p.prioridade === 'Semana 2' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {p.prioridade}
                </span>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{p.acao}</p>
                  <p className="mt-1 text-sm text-slate-500">{p.impacto}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO DASHBOARD */}
      <DashboardDemo />

      {/* CTA */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-rose-950 px-6 py-20 text-white">
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Este relatorio e a primeira entrega do Projeto 1
            </h2>
            <p className="mb-8 text-lg text-slate-300">
              O diagnostico esta validado com dados reais. O proximo passo e comecar
              a construir o banco de dados proprietario e a regua de relacionamento
              automatizada que vai resolver cada um desses problemas.
            </p>
            <Link
              href="/boticario"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-lg font-semibold text-slate-900 transition hover:bg-slate-100"
            >
              Ver proposta completa
              <ArrowRight className="h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 py-8 text-center text-sm text-slate-400">
        <p>Relatorio gerado em 12/04/2026 via auditoria automatizada ManyChat API + navegacao direta</p>
        <p className="mt-1">Conta: Espaco Do Revendedor | ID: fb3915964 | eBuz Negocios Digitais</p>
      </footer>
    </div>
  )
}
